import Axios, {AxiosResponse} from "axios";
import QueryOptions from "../sal-ui/QueryOptions";
import PagedResult from "./PagedResult";
import SbomComponent from "../domain/SbomComponent";
import SbomComponentFinding from "../domain/SbomComponentFinding";
import {FindingSeverity} from "../domain/FindingSeverity";
import {FindingStatus} from "../domain/FindingStatus";

export interface SbomComponentFindingOption {
    id: string;
    label: string;
    cveIds?: string[];
    severity?: FindingSeverity;
    status?: FindingStatus;
    sourcePublished?: string;
    position: number;
}

export interface SbomComponentOption {
    component: SbomComponent;
    position: number;
}

export interface SbomComponentFindingOptionFilters {
    severities?: FindingSeverity[];
    statuses?: FindingStatus[];
    sort?: SbomComponentFindingSort;
}

export enum SbomComponentFindingSort {
    MOST_IMPORTANT = "MOST_IMPORTANT",
    LEAST_IMPORTANT = "LEAST_IMPORTANT",
    NEWEST = "NEWEST",
    OLDEST = "OLDEST"
}

class SbomComponentService {

    public evaluateComponents(sbomProjectVersionId: string) {
        return Axios.post(`/sbom-project-versions/${sbomProjectVersionId}/evaluate`);
    }

    public importCycloneDxJsonBom(sbomProjectVersionId: string, sbom: string, type: string) {
        return Axios.put(`/sbom-project-versions/${sbomProjectVersionId}/sbom`, sbom, {headers: {'Content-Type': type}});
    }

    public getUnovisGraph(sbomComponentId: string): Promise<AxiosResponse<any>> {
        return Axios.get(`/sbom-components/${sbomComponentId}/unovis-graph`);
    }

    public updateFinding(findingId: string, finding: SbomComponentFinding) {
        return Axios.put(`/sbom-component-findings/${findingId}`, finding);
    }

    public get(sbomComponentId: string): Promise<SbomComponent> {
        return Axios.get(`/sbom-components/${sbomComponentId}`)
            .then(value => SbomComponent.fromPOJO(value.data));
    }

    public getFindings(sbomComponentId: string,
                       queryOptions: QueryOptions,
                       sort: SbomComponentFindingSort = SbomComponentFindingSort.MOST_IMPORTANT): Promise<PagedResult<SbomComponentFinding>> {
        const params = new URLSearchParams(queryOptions.toQueryParams());

        params.set("sort", sort);

        return Axios.get(`/sbom-components/${sbomComponentId}/findings?${params}`)
            .then(value => new PagedResult(
                value.data.total,
                value.data.data.map((objectJson: object) => SbomComponentFinding.fromPOJO(objectJson))
            ));
    }

    public getFindingOptions(sbomComponentId: string,
                             queryOptions: QueryOptions,
                             search: string,
                             filters: SbomComponentFindingOptionFilters = {}): Promise<PagedResult<SbomComponentFindingOption>> {
        const params = new URLSearchParams(queryOptions.toQueryParams());

        if (search !== "") {
            params.set("search", search);
        }

        filters.severities?.forEach(severity => params.append("severity", severity));
        filters.statuses?.forEach(status => params.append("status", status));

        if (filters.sort !== undefined) {
            params.set("sort", filters.sort);
        }

        return Axios.get(`/sbom-components/${sbomComponentId}/findings/options?${params}`)
            .then(value => new PagedResult(value.data.total, value.data.data));
    }

    public getList(sbomProjectVersionId: string, queryOptions: QueryOptions): Promise<PagedResult<SbomComponent>> {
        return Axios.get(`/sbom-project-versions/${sbomProjectVersionId}/components?${queryOptions.toQueryParams()}`)
            .then(value => {
                const objectList: SbomComponent[] = [];

                value.data.data.forEach((objectJson: object) => {
                    objectList.push(SbomComponent.fromPOJO(objectJson));
                });

                return new PagedResult(value.data.total, objectList);
            });
    }

    public getComponentOptions(sbomProjectVersionId: string,
                               top: number,
                               skip: number,
                               search: string,
                               componentId?: string): Promise<PagedResult<SbomComponentOption>> {
        const params = new URLSearchParams();

        params.set("$top", String(top));
        params.set("$skip", String(skip));

        if (search !== "") {
            params.set("search", search);
        }

        if (componentId !== undefined) {
            params.set("componentId", componentId);
        }

        return Axios.get(`/sbom-project-versions/${sbomProjectVersionId}/components/options?${params}`)
            .then(value => new PagedResult(
                value.data.total,
                value.data.data.map(option => ({
                    component: SbomComponent.fromPOJO(option.component),
                    position: option.position
                }))
            ));
    }

    public getListAll(queryOptions: QueryOptions): Promise<PagedResult<SbomComponent>> {
        return Axios.get(`/sbom-components?${queryOptions.toQueryParams()}`)
            .then(value => {
                const objectList: SbomComponent[] = [];

                value.data.data.forEach((objectJson: object) => {
                    objectList.push(SbomComponent.fromPOJO(objectJson));
                });

                return new PagedResult(value.data.total, objectList);
            });
    }

    public getLicenses(): Promise<string[]> {
        return Axios.get("/sbom-components/licenses").then(response => response.data);
    }

}

export default SbomComponentService;
